import API from "store/api"
import { getUser } from "actions/user"
import {
  LOGGING_IN,
  LOGIN_ERROR,
  LOGIN_SUCCESS,
  SET_USER,
  UPDATE_LOGGED_IN_USER,
  REGISTERING,
  REGISTER_ERROR,
  REGISTER_SUCCESS,
  UPDATE_LOCATION
} from "types/actions/authentication"
import { getCurrentOrg } from "actions/organization"
import { saveToken, clearToken } from "utils/cacheToken"

export const loggingIn = (loggingIn = true) => ({
  type: LOGGING_IN,
  loggingIn
})

export const loginError = (error = false) => ({
  type: LOGIN_ERROR,
  error
})

export const _loginSuccess = (user = {}, token = "") => ({
  type: LOGIN_SUCCESS,
  user,
  token
})

export const setUser = (user = {}) => ({
  type: SET_USER,
  user
})

export const updateLoggedInUser = (updates = {}) => ({
  type: UPDATE_LOGGED_IN_USER,
  updates
})

export const registering = (registering = true) => ({
  type: REGISTERING,
  registering
})

export const registerError = (error = false) => ({
  type: REGISTER_ERROR,
  error
})

export const registerSuccess = (user = {}, token = "") => ({
  type: REGISTER_SUCCESS,
  user,
  token
})

export const updateLocation = (location = {}) => ({
  type: UPDATE_LOCATION,
  location
})

export const logout = () => dispatch => {
  dispatch({ type: "LOGOUT" })
  setTimeout(() => {
    localStorage.clear()
    clearToken()
    window.location.href = "/login"
  })
}

export const loginSuccess = (user = {}, token = "") => {
  return dispatch => {
    dispatch(_loginSuccess(user, token))
    return saveToken(user.id, token)
  }
}

export const login = data => {
  return dispatch => {
    dispatch(loggingIn())
    const deferred = new Promise((resolve, reject) => {
      API.post("/auth")
        .set("Content-Type", "application/json")
        .send(data)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }

          return response
        })
        .then(response => response.body)
        .then(data => {
          if (data.message === "auth success") {
            API.set("Authorization", data.token)
            dispatch(getUser(data.id, data.token, true))
              .then(getUserResponse => {
                dispatch(loggingIn(false))
                dispatch(loginSuccess(getUserResponse, data.token))
                dispatch(getCurrentOrg())
                resolve({ ...getUserResponse, token: data.token })
              })
              .catch(reject)
          } else if (data.message === "auth failed") {
            let err
            if (data.type === "pass") {
              // bad password
              err = "The password was wrong"
            } else {
              // bad pin
              err = "The pin number was wrong"
            }
            dispatch(loggingIn(false))
            dispatch(loginError(true))
            reject(err)
          } else {
            dispatch(loggingIn(false))
            dispatch(loginError(true))
            reject(data)
          }
        })
        .catch(err => {
          dispatch(loggingIn(false))
          dispatch(loginError(true))
          if (err && err.response && err.response.body) {
            reject(err.response.body)
          } else {
            reject(err)
          }
        })
    })
    return deferred
  }
}

export const register = data => {
  return dispatch => {
    dispatch(registering())
    const deferred = new Promise((resolve, reject) => {
      API.post("/users")
        .set("Content-Type", "application/json")
        .send(data)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response
        })
        .then(response => response.body)
        .then(data => {
          if (data.message === "users create success") {
            API.set("Authorization", data.token)
            dispatch(getUser(data.id, data.token, true))
              .then(getUserResponse => {
                dispatch(registering(false))
                dispatch(registerSuccess(getUserResponse, data.token))
                resolve({ ...getUserResponse, token: data.token })
              })
              .catch(err => {
                reject(err)
              })
          } else if (data.message === "users create error") {
            let err
            if (
              data.error.hasOwnProperty("sqlMessage") &&
              data.error.sqlMessage.indexOf("Duplicate entry") > -1
            ) {
              // bad password
              err = "That phone number already exists"
            } else {
              // bad pin
              err = "There was an error registering"
            }
            dispatch(registering(false))
            dispatch(registerError(true))
            reject(err)
          }
        })
        .catch(err => {
          dispatch(registering(false))
          dispatch(registerError(true))
          reject(err.response.body)
        })
    })
    return deferred
  }
}

export const refreshToken = currentToken => {
  return dispatch => {
    return API.get("/auth")
      .then(response => {
        if (!response.ok) {
          throw Error(response.statusText)
        }

        return response
      })
      .then(response => response.body)
      .then(data => {
        dispatch(loginSuccess(undefined, data.token))
        API.set("Authorization", data.token)
        return data
      })
      .catch(data => dispatch(loginError(true)))
  }
}

export const sendMagicLink = userId => {
  return dispatch => {
    return API.get(`/users/${userId}/link`)
      .then(response => {
        if (!response.ok) {
          throw Error(response.statusText)
        }

        dispatch(loggingIn(false))

        return response
      })
      .then(response => response.body)
  }
}

export const sendPin = phone => {
  return dispatch => {
    const deferred = new Promise((resolve, reject) => {
      API.post(`/users/pin`)
        .send({ phone: phone })
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }

          dispatch(loggingIn(false))

          return response
        })
        .then(response => response.body)
        .then(response => {
          if (response.message === "users generate pin success") {
            resolve(response)
          } else {
            reject(response)
          }
        })
        .catch(err => {
          reject(err.response.body)
        })
    })
    return deferred
  }
}

export const magicLink = magicLink => {
  return dispatch => {
    dispatch(loggingIn())
    const deferred = new Promise((resolve, reject) => {
      API.post("/auth/link")
        .set("Content-Type", "application/json")
        .send({ magic_link: magicLink })
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }

          return response
        })
        .then(response => response.body)
        .then(data => {
          if (data.message === "auth success") {
            API.set("Authorization", data.token)
            dispatch(getUser(data.id, true))
              .then(getUserResponse => {
                dispatch(loggingIn(false))
                dispatch(loginSuccess(getUserResponse, data.token))
                resolve(getUserResponse)
              })
              .catch(reject)
          } else if (data.message === "auth failed") {
            let err
            if (data.type === "pass") {
              // bad password
              err = "The password was wrong"
            } else {
              // bad pin
              err = "The pin number was wrong"
            }
            dispatch(loggingIn(false))
            dispatch(loginError(true))
            reject(err)
          }
        })
        .catch(data => {
          if (data.status === 404) {
            reject()
          } else {
            reject()
          }
          dispatch(loggingIn(false))
          dispatch(loginError(true))
          reject(data)
        })
    })
    return deferred
  }
}

export const socialLogin = data => {
  return dispatch => {
    const deferred = new Promise((resolve, reject) => {
      API.post("/auth/social")
        .set("Content-Type", "application/json")
        .send(data)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }

          return response
        })
        .then(response => response.body)
        .then(data => {
          if (data.message === "auth success") {
            API.set("Authorization", data.token)
            dispatch(getUser(data.id, true))
              .then(getUserResponse => {
                dispatch(loginSuccess(getUserResponse, data.token))
                resolve(getUserResponse)
              })
              .catch(reject)
          } else if (data.message === "auth failed") {
            let err
            if (data.type === "pass") {
              // bad password
              err = "Invalid Mobile and Password."
            } else {
              // bad pin
              err = "The pin number was wrong"
            }
            reject(err)
          }
        })
        .catch(data => {
          if (data.status === 404) {
            reject({ err: "not found" })
          } else {
            reject()
          }
          reject(data)
        })
    })
    return deferred
  }
}

export const checkPin = (userId, pin) => {
  return dispatch => {
    return API.post(`/users/${userId}/pin`)
      .send({
        pin
      })
      .then(response => {
        if (!response.ok) {
          throw Error(response.statusText)
        }

        return response
      })
  }
}

export const checkResetPinExists = pin => dispatch => {
  const deferred = new Promise((resolve, reject) => {
    return API.get(`/users/pin/${pin}`)
      .then(response => {
        resolve(response.body.data)
      })
      .catch(error => {
        reject(error.message)
      })
  })
  return deferred
}

export const magicAuth = (id, magic) => dispatch => {
  const deferred = new Promise((resolve, reject) => {
    return API.post(`/auth/magic/${id}`)
      .send({ magic_link: magic })
      .then(response => {
        resolve(response.body)
      })
      .catch(error => {
        console.log(error)
        reject(error.message)
      })
  })
  return deferred
}



// WEBPACK FOOTER //
// ./src/actions/authentication.js